import Context from "../../context/Context";
import Dataset from "./Dataset";
import FieldNames from "./FieldNames";
import * as aiimUtil from "../util/aiimUtil";

export default class Levels extends Dataset {

  facilityIdField = FieldNames.FACILITY_ID;
  facilityNameField = FieldNames.FACILITY_NAME;
  levelIdField = FieldNames.LEVEL_ID;
  levelNameField = FieldNames.NAME;
  levelNumberField = FieldNames.LEVEL_NUMBER;
  levelShortNameField = FieldNames.NAME_SHORT;
  requiresFacilityMode = true;
  siteIdField = FieldNames.SITE_ID;
  siteNameField = FieldNames.SITE_NAME;
  type = "feature";
  verticalOrderField = FieldNames.VERTICAL_ORDER;

  _data = null;

  checkSchema() {
    const promise = new Promise((resolve,reject) => {
      if (this.layer2D) {
        const layer = this.layer2D;
        layer.when(() => {
          this.checkFieldNameProperty("facilityIdField",layer.fields);
          this.checkFieldNameProperty("facilityNameField",layer.fields);
          this.checkFieldNameProperty("levelIdField",layer.fields);
          this.checkFieldNameProperty("levelNameField",layer.fields);
          this.checkFieldNameProperty("levelNumberField",layer.fields);
          this.checkFieldNameProperty("levelShortNameField",layer.fields);
          this.checkFieldNameProperty("siteIdField",layer.fields);
          this.checkFieldNameProperty("siteNameField",layer.fields);
          this.checkFieldNameProperty("verticalOrderField",layer.fields);
          if (!this._data) this._queryAll();
          resolve();
        }).catch(ex => {
          console.log("Failed to load dataset layer:",layer.title);
          console.error(ex);
          resolve();
        });
      } else {
        resolve();
      }
    });
    return promise;
  }

  fixZ(source,feature) {
    const geometry = feature && feature.geometry;
    if (geometry && geometry.type === "point" && !geometry.hasZ) {
      const zInfo = this.getZInfo(source,feature);
      if (zInfo && zInfo.levelData) {
        const z = zInfo.levelData.z;
        if (typeof z === "number") {
          geometry.z = z;
          geometry.hasZ = true;
        }
      }
    }
  }

  getFacilities() {
    return this._data && this._data.facilities;
  }

  getFacilityData(facilityId) {
    if (typeof facilityId === "string" && facilityId.length > 0) {
      if (this._data && this._data.dataByFacility) {
        return this._data.dataByFacility[facilityId];
      }
    }
    return null;
  }

  getFirstCentroid(){
    let facilities = this._data && this._data.facilities;
    if(facilities && facilities.length > 0){
      let facilityID = this._data.facilities[0].facilityId;
      const facilitiesFootprints = Context.getInstance().aiim.facilityFootprints;
      if(facilitiesFootprints && facilitiesFootprints.graphicsByFacilityId){
        let centroid = facilitiesFootprints.graphicsByFacilityId[facilityID].geometry.centroid;
        return centroid;
      }
    }
  }

  getShortNameIfLoaded(facilityId,levelNumber) {
    if (this._data && this._data.dataByFacility) {
      const facilityData = this._data.dataByFacility[facilityId];
      if (facilityData && facilityData.shortNamesByLevelNumber) {
        const sn = facilityData.shortNamesByLevelNumber[levelNumber];
        //console.log("==sn==",sn);
        return sn;
      }
    }
    if (typeof levelNumber === "number") return ""+levelNumber;
    return "";
  }

  getZeroVOLevel(facilityData) {
    if (!facilityData) return null;
    if (facilityData.zeroVOLevel) return facilityData.zeroVOLevel;
    facilityData.levels.some(level => {
      const baseVO = (facilityData.baseVO || 0);
      if (level.verticalOrder === baseVO) {
        if (typeof level.levelNumber === "number") {
          facilityData.zeroVOLevel = level;
        }
        return true;
      }
      return false;
    });
    return facilityData.zeroVOLevel;
  }

  getZInfo(source,feature) {

    const chkStr = (v) => {
      return (typeof v === "string" && v.length > 0);
    };

    const getField = (name,defaultValue) => {
      if (source && source.mappings &&
        source.mappings.hasOwnProperty(name) && source.mappings[name]) {
        return source.mappings[name];
      }
      return defaultValue;
    };

    const data = this._data;
    const attributes = feature && feature.attributes;
    const facilityIdField = getField("facilityIdField",FieldNames.FACILITY_ID);
    const facilityNameField = getField("facilityNameField",FieldNames.FACILITY_NAME);
    const levelIdField = getField("levelIdField",FieldNames.LEVEL_ID);
    const levelNameField = getField("levelNameField",FieldNames.LEVEL_NAME);
    const levelNumberField = getField("levelNumberField",FieldNames.LEVEL_NUMBER);
    const verticalOrderField = getField("levelNumberField",FieldNames.VERTICAL_ORDER);
    let facilityData, levelData;

    if (!levelData && chkStr(levelIdField)) {
      const levelId = aiimUtil.getAttributeValue(attributes,levelIdField);
      if (chkStr(levelId)) {
        levelData = data && data.levelsByLevelId[levelId];
      }
    }

    if (!levelData && !facilityData && chkStr(facilityIdField)) {
      const facilityId = aiimUtil.getAttributeValue(attributes,facilityIdField);
      facilityData = this.getFacilityData(facilityId);
    }

    if (!levelData && !facilityData && chkStr(facilityNameField)) {
      const facilityName = aiimUtil.getAttributeValue(attributes,facilityNameField);
      if (chkStr(facilityName)) {
        const facilityId = data && data.facilityIdsByName[facilityName];
        facilityData = this.getFacilityData(facilityId);
      }
    }

    if (!levelData && facilityData && chkStr(levelNumberField)) {
      const levelNumber = aiimUtil.getAttributeValue(attributes,levelNumberField);
      levelData = facilityData.levelsByLevelNumber[levelNumber];
    }

    if (!levelData && facilityData && chkStr(levelNameField)) {
      const levelName = aiimUtil.getAttributeValue(attributes,levelNameField);
      levelData = facilityData.levelsByLevelName[levelName];
    }

    if (!levelData && facilityData && chkStr(verticalOrderField)) {
      const vo = aiimUtil.getAttributeValue(attributes,verticalOrderField);
      levelData = facilityData.levelsByVO[vo];
    }

    if (levelData) {
      return {
        facilityId: levelData.facilityId,
        levelData: levelData
      }
    }
    return null;
  }

  hasData() {
    return !!this._data;
  }

  load() {
    return this._queryAll();
  }

  _queryAll() {
    if (this._data) {
      return Promise.resolve(this._data);
    }
    const dataByFacility = {}, levelsByLevelId = {};
    const facilities = [], facilityIdsByName = {};
    const siteIdField = this.siteIdField;
    const siteNameField = this.siteNameField;
    const facilityIdField = this.facilityIdField;
    const facilityNameField = this.facilityNameField;
    const levelIdField = this.levelIdField;
    const levelNameField = this.levelNameField;
    const levelNumberField = this.levelNumberField;
    const levelShortNameField = this.levelShortNameField;
    const verticalOrderField = this.verticalOrderField;

    const url = Context.checkMixedContent(this.url);
    const lib = Context.getInstance().lib;
    const task = new lib.esri.QueryTask({url: url});
    const query = new lib.esri.Query();
    query.outFields = ["*"];
    query.orderByFields = [verticalOrderField]
    query.returnGeometry = true;
    query.returnZ = true;
    query.where = "1=1";

    //console.log("Levels - loading all...");
    return task.execute(query).then((result) => {
      //console.log("Levels.result",result);
      if (result && result.features) {

        const elevationA = aiimUtil.findField(result.fields,"elevation_absolute");
        const elevationR = aiimUtil.findField(result.fields,"elevation_relative");
        const heightA = aiimUtil.findField(result.fields,"height_absolute");
        const heightR = aiimUtil.findField(result.fields,"height_relative");

        result.features.forEach((feature) => {
          if (feature && feature.attributes) {
            //console.log("======level=",feature.attributes);
            let attributes = feature.attributes;
            const siteId = aiimUtil.getAttributeValue(feature.attributes,siteIdField);
            const siteName = aiimUtil.getAttributeValue(feature.attributes,siteNameField);
            const facilityId = aiimUtil.getAttributeValue(feature.attributes,facilityIdField);
            const facilityName = aiimUtil.getAttributeValue(feature.attributes,facilityNameField);
            const levelName = aiimUtil.getAttributeValue(feature.attributes,levelNameField);
            const levelId = aiimUtil.getAttributeValue(feature.attributes,levelIdField);
            const levelNumber = aiimUtil.getAttributeValue(feature.attributes,levelNumberField);
            const sn = aiimUtil.getAttributeValue(feature.attributes,levelShortNameField);
            const vo = aiimUtil.getAttributeValue(feature.attributes,verticalOrderField);
            const centroid = feature.geometry && feature.geometry.centroid;
            const z = (centroid && typeof centroid.z === "number") ? centroid.z : 0;

            if (levelsByLevelId[levelId]) {
              console.warn("Levels: "+levelIdField+"="+levelId+" is not unique");
              //console.warn("levelData",levelData);
              //console.warn("levelsByLevelId[levelId]",levelsByLevelId[levelId]);
              return;
            }

            if (typeof facilityId === "string" && facilityId.length > 0) {
              let facilityData;
              if (dataByFacility.hasOwnProperty(facilityId)) {
                facilityData = dataByFacility[facilityId];
              } else {
                facilityData = {
                  siteId: siteId,
                  siteName: siteName,
                  facilityId: facilityId,
                  facilityName: facilityName,
                  levels: [],
                  levelsByLevelName: {},
                  levelsByLevelNumber: {},
                  levelsByVO: {},
                  shortNamesByLevelNumber: {},
                  levelNumbersByVO: {},
                  zeroVOLevel: null,
                  baseVO: null
                };
                dataByFacility[facilityId] = facilityData;
                facilities.push(facilityData);

                // for testing long building names
                // if (facilityData.facilityName === "O") {
                //   facilityData.facilityName = "A very very very very very long building name name name name name";
                // }

              }
              const levelData = {
                siteId: siteId,
                siteName: siteName,
                facilityId: facilityId,
                facilityName: facilityName,
                levelId: levelId,
                levelName: levelName,
                levelNumber: levelNumber,
                levelShortName: sn,
                verticalOrder: vo,
                z: z,
                elevationAbsolute: elevationA && aiimUtil.getAttributeValue(attributes,elevationA.name),
                elevationRelative: elevationR && aiimUtil.getAttributeValue(attributes,elevationR.name),
                heightAbsolute: heightA && aiimUtil.getAttributeValue(attributes,heightA.name),
                heightRelative: heightR && aiimUtil.getAttributeValue(attributes,heightR.name)
              }
              facilityData.levels.push(levelData);
              facilityData.levelsByLevelName[levelName] = levelData;
              facilityData.levelsByLevelNumber[levelNumber] = levelData;
              facilityData.levelsByVO[vo] = levelData;
              facilityData.shortNamesByLevelNumber[levelNumber] = sn;
              facilityData.levelNumbersByVO[vo] = levelNumber;
              if (!facilityIdsByName[facilityName]) {
                facilityIdsByName[facilityName] = facilityId;
              }
              levelsByLevelId[levelId] = levelData;

              if (typeof vo === "number" && isFinite(vo)) {
                const baseVO = facilityData.baseVO;
                if (vo >= 0) {
                  if (baseVO === null || baseVO < 0 || vo < baseVO) {
                    facilityData.baseVO = vo;
                    facilityData.zeroVOLevel = levelData;
                  }
                } else if (vo < 0) {
                  if (baseVO === null || (baseVO < 0 && vo > baseVO)) {
                    facilityData.baseVO = vo;
                    facilityData.zeroVOLevel = levelData;
                  }
                }
              }

            }
          }
        });
      }
      facilities.sort((a,b) => a.facilityName.localeCompare(b.facilityName));
      const data = {
        facilities: facilities,
        dataByFacility: dataByFacility,
        facilityIdsByName: facilityIdsByName,
        levelsByLevelId: levelsByLevelId
      }
      if (!this._data) this._data = data;
      //console.log("========= Levels:_queryAll",data);
      return data;
    }).catch(ex => {
      console.log("Error querying all levels:");
      console.error(ex);
      return Promise.reject(ex);
    });
  }

  queryLevelDataByPoint(point) {
  if (!this.url || !this.layer2D) return Promise.resolve();
    const url = Context.checkMixedContent(this.url);
    const lib = Context.getInstance().lib;
    const task = new lib.esri.QueryTask({url: url});
    const query = new lib.esri.Query();
    query.outFields = ["*"];
    query.returnGeometry = false;
    query.returnZ = true;
    query.where = this.layer2D.definitionExpression || "1=1";
    query.geometry = point;
    return task.execute(query).then(result => {
      let levelData;
      let features = result && result.features;
      let f = features && features.length === 1 && features[0];
      //console.log("f",f)
      if (f && this._data && this._data.levelsByLevelId) {
       let lid = aiimUtil.getAttributeValue(f.attributes,this.levelIdField);
       if (lid) levelData = this._data.levelsByLevelId[lid];
      }
      return levelData;
    });
  }

}
